/**
 * AdminComprovantesReview — admin revisa comprovantes (fotos/PDFs) que clientes
 * enviaram pra confirmar pagamento de romaneios.
 * Backend: GET/POST /api/admin/comprovantes-cliente[/{id}/approve|reject]
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Receipt, Check, X, Clock, FileText, User, ExternalLink, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const fmtDate = (s) => { try { return new Date(s).toLocaleString('pt-BR'); } catch { return s; } };

export default function AdminComprovantesReview() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pendente_revisao');
  const [actioningId, setActioningId] = useState(null);
  const [rejectModal, setRejectModal] = useState(null); // {id, motivo}
  const [approveModal, setApproveModal] = useState(null); // {id, romaneio, mark_pago, obs}

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const r = await axios.get(`${API}/admin/comprovantes-cliente`, { params });
      setItems(r.data || []);
    } catch (e) {
      toast.error('Erro ao carregar comprovantes');
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { carregar(); }, [carregar]);

  const submitApprove = async () => {
    if (!approveModal) return;
    setActioningId(approveModal.id);
    try {
      await axios.post(`${API}/admin/comprovantes-cliente/${approveModal.id}/approve`, {
        mark_romaneio_pago: !!approveModal.mark_pago,
        observacao: approveModal.obs || '',
      });
      toast.success(approveModal.mark_pago ? 'Aprovado e romaneio marcado como PAGO!' : 'Comprovante aprovado!');
      setApproveModal(null);
      await carregar();
    } catch (e) {
      toast.error('Erro ao aprovar');
    }
    setActioningId(null);
  };

  const submitReject = async () => {
    if (!rejectModal) return;
    setActioningId(rejectModal.id);
    try {
      await axios.post(`${API}/admin/comprovantes-cliente/${rejectModal.id}/reject`, {
        motivo: rejectModal.motivo || '',
      });
      toast.success('Comprovante rejeitado');
      setRejectModal(null);
      await carregar();
    } catch (e) {
      toast.error('Erro ao rejeitar');
    }
    setActioningId(null);
  };

  const statusLabel = {
    pendente_revisao: { txt: 'Pendente', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
    aprovado: { txt: 'Aprovado', cls: 'bg-green-100 text-green-800 border-green-300' },
    rejeitado: { txt: 'Rejeitado', cls: 'bg-red-100 text-red-800 border-red-300' },
  };

  return (
    <div className="space-y-4" data-testid="admin-comprovantes-review-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt className="w-6 h-6 text-primary" />
            Comprovantes de Pagamento
          </h1>
          <p className="text-sm text-muted-foreground">Revise comprovantes enviados pelos clientes/vendedores.</p>
        </div>
        <div className="flex gap-2">
          {[
            { v: 'pendente_revisao', label: 'Pendentes' },
            { v: 'aprovado', label: 'Aprovados' },
            { v: 'rejeitado', label: 'Rejeitados' },
            { v: '', label: 'Todos' },
          ].map(opt => (
            <button
              key={opt.v || 'all'}
              onClick={() => setFilter(opt.v)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
                filter === opt.v
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border hover:bg-accent'
              }`}
              data-testid={`filter-${opt.v || 'all'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground mt-3">Carregando...</p>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-xl" data-testid="comprovantes-empty">
          <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Nenhum comprovante {filter ? `com status "${filter}"` : ''} encontrado.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map(c => {
            const isImg = (c.mime_type || '').startsWith('image/');
            const fileUrl = (c.url || '').startsWith('http')
              ? c.url
              : `${process.env.REACT_APP_BACKEND_URL}${c.url}`;
            const st = statusLabel[c.status] || { txt: c.status, cls: 'bg-gray-100 text-gray-700' };
            return (
              <div
                key={c.id}
                className="bg-card border border-border rounded-xl p-4 flex flex-col md:flex-row gap-4"
                data-testid={`comprovante-card-${c.id}`}
              >
                <div className="md:w-48 flex-shrink-0">
                  {isImg ? (
                    <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                      <img
                        src={fileUrl}
                        alt={c.filename}
                        className="w-full h-32 object-cover rounded-lg border border-border hover:opacity-80"
                      />
                    </a>
                  ) : (
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full h-32 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/40 hover:bg-muted text-muted-foreground"
                    >
                      <FileText className="w-8 h-8 mb-2" />
                      <span className="text-xs">Abrir arquivo</span>
                    </a>
                  )}
                </div>

                <div className="flex-1 min-w-0 space-y-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full border ${st.cls}`}>
                      {st.txt}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {fmtDate(c.created_at)}
                    </span>
                  </div>
                  <p className="text-sm">
                    <User className="w-3.5 h-3.5 inline mr-1 text-muted-foreground" />
                    <b>{c.uploader_name || 'Sem nome'}</b>{' '}
                    <span className="text-xs text-muted-foreground">({c.uploader_role})</span>
                  </p>
                  {c.romaneio && (
                    <p className="text-sm">
                      <FileText className="w-3.5 h-3.5 inline mr-1 text-muted-foreground" />
                      Romaneio <b>#{c.romaneio.numero_pedido}</b> — {c.romaneio.cliente_nome}{' '}
                      <span className="text-primary font-semibold">{fmtBRL(c.romaneio.valor_total)}</span>{' '}
                      <span className="text-xs text-muted-foreground">({c.romaneio.status_pagamento || 'em aberto'})</span>
                    </p>
                  )}
                  {c.descricao && (
                    <p className="text-xs text-muted-foreground italic">&ldquo;{c.descricao}&rdquo;</p>
                  )}
                  {c.status === 'rejeitado' && c.review_motivo && (
                    <p className="text-xs text-red-700 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded">
                      ❌ Motivo: {c.review_motivo}
                    </p>
                  )}
                  {c.status === 'aprovado' && c.review_observacao && (
                    <p className="text-xs text-green-700 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded">
                      ✓ {c.review_observacao}
                    </p>
                  )}
                  {c.reviewed_by && (
                    <p className="text-[10px] text-muted-foreground">
                      Revisado por {c.reviewed_by} em {fmtDate(c.reviewed_at)}
                    </p>
                  )}
                </div>

                {c.status === 'pendente_revisao' && (
                  <div className="flex md:flex-col gap-2 md:w-32 flex-shrink-0">
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 px-3 py-1.5 text-xs font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 flex items-center justify-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" /> Ver
                    </a>
                    <button
                      disabled={actioningId === c.id}
                      onClick={() => setApproveModal({ id: c.id, romaneio: c.romaneio, mark_pago: false, obs: '' })}
                      className="flex-1 px-3 py-1.5 text-xs font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-1"
                      data-testid={`approve-btn-${c.id}`}
                    >
                      <Check className="w-3 h-3" /> Aprovar
                    </button>
                    <button
                      disabled={actioningId === c.id}
                      onClick={() => setRejectModal({ id: c.id, motivo: '' })}
                      className="flex-1 px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1"
                      data-testid={`reject-btn-${c.id}`}
                    >
                      <X className="w-3 h-3" /> Rejeitar
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal Aprovar */}
      {approveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md p-5 space-y-3">
            <h3 className="font-bold text-lg flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600" /> Aprovar Comprovante
            </h3>
            {approveModal.romaneio && (
              <p className="text-sm text-muted-foreground">
                Romaneio <b>#{approveModal.romaneio.numero_pedido}</b> — {fmtBRL(approveModal.romaneio.valor_total)}
              </p>
            )}
            <label className="flex items-center gap-2 text-sm bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={approveModal.mark_pago}
                onChange={(e) => setApproveModal({ ...approveModal, mark_pago: e.target.checked })}
                data-testid="mark-pago-checkbox"
              />
              <span><b>Marcar romaneio como PAGO</b> (dá baixa no pagamento)</span>
            </label>
            <textarea
              placeholder="Observação (opcional)"
              value={approveModal.obs}
              onChange={(e) => setApproveModal({ ...approveModal, obs: e.target.value })}
              className="input-field text-sm h-20 resize-none"
              data-testid="approve-obs-input"
            />
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setApproveModal(null)}
                className="px-3 py-1.5 text-sm rounded hover:bg-accent"
              >
                Cancelar
              </button>
              <button
                onClick={submitApprove}
                disabled={actioningId === approveModal.id}
                className="px-4 py-1.5 text-sm font-semibold bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                data-testid="confirm-approve-btn"
              >
                Confirmar Aprovação
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Rejeitar */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md p-5 space-y-3">
            <h3 className="font-bold text-lg flex items-center gap-2">
              <X className="w-5 h-5 text-red-600" /> Rejeitar Comprovante
            </h3>
            <p className="text-xs text-muted-foreground">
              Informe o motivo. Ele ficará visível pro cliente.
            </p>
            <textarea
              placeholder="Ex: imagem ilegível, valor não confere..."
              value={rejectModal.motivo}
              onChange={(e) => setRejectModal({ ...rejectModal, motivo: e.target.value })}
              className="input-field text-sm h-20 resize-none"
              autoFocus
              data-testid="reject-motivo-input"
            />
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setRejectModal(null)}
                className="px-3 py-1.5 text-sm rounded hover:bg-accent"
              >
                Cancelar
              </button>
              <button
                onClick={submitReject}
                disabled={actioningId === rejectModal.id}
                className="px-4 py-1.5 text-sm font-semibold bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                data-testid="confirm-reject-btn"
              >
                Confirmar Rejeição
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
